import type { CompiledChapter } from "./schema/compiled";

/** Whether anyone in the chapter is there before the reader can know it. */
export function hasSecretPresence(chapter: CompiledChapter): boolean {
  return chapter.scenes.some((scene) => (
    scene.participants.some((person) => person.secret === true)
  ));
}

/**
 * The chapter as a reader without spoilers sees it: people who are there in secret, such as
 * someone hiding as a flea, are left out until the story reveals them.
 */
export function withoutSecretPresence(chapter: CompiledChapter): CompiledChapter {
  if (!hasSecretPresence(chapter)) {
    return chapter;
  }
  const hiddenBySceneId = new Map(chapter.scenes.map((scene) => [
    scene.id,
    new Set(scene.participants.filter((person) => person.secret === true)
      .map((person) => person.characterId)),
  ] as const));
  const rail = (layout: CompiledChapter["railsByJourney"]["book"]) => ({
    ...layout,
    stays: layout.stays.filter((stay) => stay.secret !== true),
  });
  const events = (list: CompiledChapter["eventsByJourney"]["book"]) => list.map((event) => {
    const hidden = hiddenBySceneId.get(event.sceneId);
    return hidden === undefined || hidden.size === 0
      ? event
      : { ...event, characterIds: event.characterIds.filter((id) => !hidden.has(id)) };
  });
  return {
    ...chapter,
    scenes: chapter.scenes.map((scene) => ({
      ...scene,
      participants: scene.participants.filter((person) => person.secret !== true),
    })),
    eventsByJourney: {
      book: events(chapter.eventsByJourney.book),
      universe: events(chapter.eventsByJourney.universe),
    },
    railsByJourney: {
      book: rail(chapter.railsByJourney.book),
      universe: rail(chapter.railsByJourney.universe),
    },
  };
}
